import { CalendarConfiguration, RentalPeriod } from '../../types/rental'

/**
 * Represents a single product in the configurator step
 * Handles product-specific selection, state, and calendar configuration
 */
export class ConfiguratorProduct {
  private _element: HTMLElement
  private selectTrigger: HTMLElement
  private calendarConfig: CalendarConfiguration | null = null
  private onSelect: (productId: string, variantId: number) => void
  private isSelected: boolean = false
  protected selectedVariantId: number | null = null
  private variantImages: { variants: Array<{ id: number, image: string | null }> } | null = null

  /**
   * Creates a new ConfiguratorProduct instance
   * @param element - The product's root HTML element
   * @param onSelect - Callback function when product is selected
   */
  constructor(element: HTMLElement, onSelect: (productId: string, variantId: number) => void) {
    this._element = element
    this.onSelect = onSelect
    this.selectTrigger = element.querySelector('.product-configurator__select-trigger') as HTMLElement
    this.initializeCalendarConfig()
    this.initializeVariantImages()
    this.initializeListeners()
  }

  /**
   * Initializes the calendar configuration from JSON data
   */
  private initializeCalendarConfig() {
    const calendarJson = this._element.querySelector('.product-configurator__calendar-json')?.textContent
    if (calendarJson?.trim()) {
      try {
        this.calendarConfig = JSON.parse(calendarJson)
      } catch (error) {
        console.error('Failed to parse calendar configuration:', error)
      }
    }
  }

  /**
   * Initializes the variant images from JSON data
   */
  private initializeVariantImages() {
    const variantImagesJson = this._element.querySelector('.product-configurator__variant-images')?.textContent
    if (variantImagesJson?.trim()) {
      try {
        this.variantImages = JSON.parse(variantImagesJson)

      } catch (error) {
        console.error('Failed to parse variant images:', error)
      }
    }
  }

  /**
   * Updates the product image based on the selected variant
   * @param variantId - The ID of the selected variant
   */
  private updateProductImage(variantId: number) {

    
    // Get all variant images
    const allImages = this._element.querySelectorAll('.product-configurator__step-product-image-img')


    // First hide all images
    allImages.forEach(img => {
      img.classList.remove('product-configurator__step-product-image-img--active')
    })

    // Try to find and show the variant image
    const variantImage = this._element.querySelector(`.product-configurator__step-product-image-img[data-variant-id="${variantId}"]`)
    if (variantImage) {

      variantImage.classList.add('product-configurator__step-product-image-img--active')
    } else {
      // If no variant image found, show the default product image

      const defaultImage = this._element.querySelector('.product-configurator__step-product-image-img[data-variant-id="default"]')
      if (defaultImage) {
        defaultImage.classList.add('product-configurator__step-product-image-img--active')
      }
    }
  }

  /**
   * Gets a rental period by its duration
   * @param duration - Duration in days
   * @returns Matching rental period or null if not found
   */
  getRentalPeriodByDuration(duration: number): RentalPeriod | null {
    return this.calendarConfig?.rental_periods.find(period => period.duration_count === duration) || null
  }

  /**
   * Gets the product's Shopify ID
   * @returns Product ID or undefined if not set
   */
  get productId(): string {
    return this._element.dataset.productId || ''
  }

  /**
   * Initializes event listeners for product interactions
   */
  private initializeListeners() {
    this.selectTrigger?.addEventListener('click', (e) => {
      e.stopPropagation()

      // Close any other open selects first
      const allSelects = document.querySelectorAll('.product-configurator__select--active')
      allSelects.forEach(select => {
        if (select !== this.selectTrigger.closest('.product-configurator__select')) {
          select.classList.remove('product-configurator__select--active')
        }
      })

      // Toggle this select
      const select = this.selectTrigger.closest('.product-configurator__select')
      select?.classList.toggle('product-configurator__select--active')

      // Update position if open
      this.updateSelectListPosition()
    })

    // Add resize listener
    window.addEventListener('resize', () => {
      this.updateSelectListPosition()
    })

    const selectListItems = this._element.querySelectorAll('.product-configurator__select-list-item')
    selectListItems.forEach(item => {
      item.addEventListener('click', (e) => this.handleVariantSelect(e))
    })
  }

  /**
   * Handles variant selection from dropdown
   * @param event - Click event from variant selection
   */
  private handleVariantSelect(event: Event) {
    let item = event.target as HTMLElement
    if(item.closest('.product-configurator__select-list-item')) {
      item = item.closest('.product-configurator__select-list-item') as HTMLButtonElement
    }

    const variantId = Number(item.dataset.value)

    if (!variantId) return

    this.selectedVariantId = variantId
    this._element.dataset.variantId = String(variantId)

    // Update the product image when variant is selected
    this.updateProductImage(variantId)

    const triggerText = this.selectTrigger.querySelector('.product-configurator__select-trigger-text')
    if (triggerText) {
      triggerText.textContent = item.querySelector('.product-configurator__select-list-item-text-title')?.textContent || 'Size'
    }

    const select = this.selectTrigger.closest('.product-configurator__select')
    select?.classList.remove('product-configurator__select--active')

    // Call onSelect immediately when variant is selected
    this.onSelect(this.productId, variantId)
  }

  /** Disables the product selection */
  disable() {
    this._element.classList.add('product-configurator__step-product--disabled')
    // Reset the selection when disabled
    this.clearSelection()
  }

  /** Enables the product selection */
  enable() {
    this._element.classList.remove('product-configurator__step-product--disabled')
  }

  /** Clears the current selection */
  clearSelection() {
    this.selectedVariantId = null
    const triggerText = this.selectTrigger.querySelector('.product-configurator__select-trigger-text')
    if (triggerText) {
      triggerText.textContent = 'Select'
    }
  }

  /** Resets the product selection state */
  reset() {
    this.selectedVariantId = null
    this.enable()
    this.clearSelection()
  }

  /**
   * Updates which variants are available for this product
   * @param availableVariantIds - Array of available variant IDs
   */
  updateAvailableVariants(availableVariantIds: number[]) {
    const variantItems = this._element.querySelectorAll('.product-configurator__select-list-item')
    variantItems.forEach(item => {
      const variantId = Number((item as HTMLElement).dataset.value)
      const isAvailable = availableVariantIds.includes(variantId)

      item.classList.toggle('product-configurator__select-list-item--disabled', !isAvailable)
      ;(item as HTMLButtonElement).disabled = !isAvailable

      const textContainer = item.querySelector('.product-configurator__select-list-item-text')

      if (!isAvailable) {
        if (!item.querySelector('.product-configurator__select-list-item-availability')) {
          const availabilitySpan = document.createElement('span')
          availabilitySpan.className = 'product-configurator__select-list-item-availability'
          availabilitySpan.textContent = 'Unavailable'
          item.appendChild(availabilitySpan)
        }
        textContainer?.classList.add('product-configurator__select-list-item-text--disabled')
      } else {
        item.querySelector('.product-configurator__select-list-item-availability')?.remove()
        textContainer?.classList.remove('product-configurator__select-list-item-text--disabled')
      }
    })
  }

  private updateSelectListPosition() {
    const select = this.selectTrigger.closest('.product-configurator__select')
    if (!select?.classList.contains('product-configurator__select--active')) return

    const productContainer = this._element
    const selectList = this._element.querySelector('.product-configurator__select-list') as HTMLElement
    const productRect = productContainer.getBoundingClientRect()
    const selectRect = this.selectTrigger.getBoundingClientRect()

    selectList.style.width = `${productRect.width}px`
    selectList.style.left = `${-(selectRect.left - productRect.left)}px`
  }

  // Don't forget to remove the listener when the component is destroyed
  destroy() {
    window.removeEventListener('resize', () => {
      this.updateSelectListPosition()
    })
  }

  get currentVariantId(): number | null {
    return this.selectedVariantId
  }

  getSelectedOptions(): Record<string, string> {
    const label = this._element.querySelector('.product-configurator__input-label')?.textContent?.trim().toLowerCase() || 'option'
    const value = this._element.querySelector('.product-configurator__select-trigger-text')?.textContent?.trim() || ''
    return { [label]: value }
  }

  get element(): HTMLElement {
    return this._element
  }
}